import { PaginationLoadMore, PaginationLoadMoreButton } from '@vs-shop/commerce-ui/pagination';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../utils/intl';

import { lastPageLoadedIncludingLoadMoreAtom } from '@vs-shop/engine/atom/products/pagination';
import type { HydraViewExtended } from '@vs-shop/schemas/openapi/hydraView';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  hydraViewExtended: HydraViewExtended;
  loadMorePages: () => void;
  isLoading?: boolean;
  /**
   * Highest page currently rendered. Defaults to the products pagination atom,
   * which listings that do not keep their own page counter rely on.
   */
  lastPageLoaded?: number;
};

/**
 * rcprofi's load-more control: the shared `@vs-shop/commerce-ui` parts fed this
 * tenant's paging.
 *
 * Whether there is anything left to append is answered here, because it is a
 * question about this tenant's page counter rather than about how the control
 * looks — the parts are composed only when the answer is yes.
 */
export default function LoadMorePagination({
  hydraViewExtended,
  loadMorePages,
  isLoading,
  lastPageLoaded,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const productsLastPageLoaded = useAtomValue(lastPageLoadedIncludingLoadMoreAtom);
  const lastPageLoadedIncludingLoadMore = lastPageLoaded ?? productsLastPageLoaded;

  // Hide button once everything up to the last page is rendered
  if (lastPageLoadedIncludingLoadMore >= hydraViewExtended.hydraViewNumbers.last) {
    return null;
  }

  const handleLoadMore = () => {
    loadMorePages();
  };

  return (
    <PaginationLoadMore>
      <PaginationLoadMoreButton onClick={handleLoadMore} loading={isLoading} disabled={isLoading}>
        {intl.formatMessage({
          id: 'container.pagination.loadMorePagination.showMore',
          defaultMessage: 'ZOBRAZIT DALŠÍ',
        })}
      </PaginationLoadMoreButton>
    </PaginationLoadMore>
  );
}
